import React, { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Checkbox,
  HStack,
  Link,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { Link as ReactRouterLink } from 'react-router-dom'

import axios from 'lib/axios'

import InfoHint from '../components/InfoHint'
import { formatDisplayDate, getErrorMessage } from '../types'

type PreviewRecipient = {
  id: string
  address: string | null
  occupancyDate?: string | null
  evictionCanceledAt?: string | null
  moveOutDate?: string | null
  cashoutStatus?: string | null
  evictionStatus?: string | null
}

type PreviewResponse = {
  previewHash: string
  eligibleCount: number
  currentlyEnabledCount: number
  newRecipients: PreviewRecipient[]
  droppedRecipients: PreviewRecipient[]
}

type Props = {
  statementEndDate: string
}

const AddressCell = ({ id, address }: Pick<PreviewRecipient, 'id' | 'address'>) => (
  <Link
    as={ReactRouterLink}
    to={`/rent-roll/${id}`}
    color='teal.500'
    target='_blank'
    rel='noreferrer'
  >
    {address || id}
  </Link>
)

const ReviewRecipients = ({ statementEndDate }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [forceApply, setForceApply] = useState(false)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)

  const onPreview = async () => {
    try {
      setIsLoading(true)
      setError('')
      setSuccessMessage('')

      const { data } = await axios.post('/admin/statements/recipients/preview', {
        statementEndDate
      })
      setPreview(data)
      setForceApply(false)
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const onApply = async () => {
    if (!preview) {
      return
    }

    try {
      setIsApplying(true)
      setError('')
      setSuccessMessage('')

      const { data } = await axios.post('/admin/statements/recipients/apply', {
        statementEndDate,
        previewHash: preview.previewHash,
        force: forceApply
      })

      setSuccessMessage(
        `Applied recipient changes${forceApply ? ' (forced)' : ''}. Enabled: ${
          data.enabledCount
        }, Disabled: ${data.disabledCount}, Unchanged: ${data.unchangedCount}.`
      )
      setIsConfirmed(false)
      await onPreview()
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Text fontSize='sm' color='gray.700'>
        Review who will receive statements this month before applying changes to
        <code> rentals.is_statements_enabled</code>.
      </Text>
      <HStack spacing={3}>
        <Button
          colorScheme='teal'
          variant='outline'
          onClick={onPreview}
          isDisabled={isLoading || !statementEndDate}
        >
          Preview Recipients
        </Button>
        <InfoHint label='Runs eligibility logic for the selected statement month and compares the result to currently enabled rentals, without writing any changes.' />
        {isLoading && <Spinner size='sm' />}
      </HStack>

      {error && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {error.includes('force=true') && !forceApply && (
        <Alert status='warning'>
          <AlertIcon />
          <AlertDescription>
            If this drop is expected, enable Force apply below and re-run apply.
          </AlertDescription>
        </Alert>
      )}
      {successMessage && (
        <Alert status='success'>
          <AlertIcon />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {preview && (
        <Stack spacing={3}>
          <Alert status='info'>
            <AlertIcon />
            <HStack spacing={1} alignItems='center'>
              <AlertTitle>
                {preview.eligibleCount} eligible ({preview.newRecipients.length} new,{' '}
                {preview.droppedRecipients.length} dropped)
              </AlertTitle>
              <InfoHint label='Eligible = rentals that should receive statements now. New = will be newly enabled. Dropped = currently enabled but should be disabled.' />
            </HStack>
          </Alert>

          <Text fontWeight='bold'>
            New Recipients
            <InfoHint label='Rentals that will be enabled for statements after Apply Recipient Changes.' />
          </Text>
          <TableContainer>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>Address</Th>
                  <Th>Occupancy Date</Th>
                  <Th>Move Out Date</Th>
                  <Th>Eviction Canceled On</Th>
                </Tr>
              </Thead>
              <Tbody>
                {preview.newRecipients.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <AddressCell id={row.id} address={row.address} />
                    </Td>
                    <Td>{formatDisplayDate(row.occupancyDate)}</Td>
                    <Td>{formatDisplayDate(row.moveOutDate)}</Td>
                    <Td>{formatDisplayDate(row.evictionCanceledAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <Text fontWeight='bold'>
            Dropped Recipients
            <InfoHint label='Rentals that are currently enabled but will be disabled after apply because they no longer meet eligibility checks.' />
          </Text>
          <TableContainer>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>Address</Th>
                  <Th>Occupancy Date</Th>
                  <Th>Move Out Date</Th>
                  <Th>Cashout</Th>
                  <Th>Eviction</Th>
                </Tr>
              </Thead>
              <Tbody>
                {preview.droppedRecipients.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <AddressCell id={row.id} address={row.address} />
                    </Td>
                    <Td>{formatDisplayDate(row.occupancyDate)}</Td>
                    <Td>{formatDisplayDate(row.moveOutDate)}</Td>
                    <Td>{row.cashoutStatus || 'N/A'}</Td>
                    <Td>{row.evictionStatus || 'N/A'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <Checkbox
            isChecked={isConfirmed}
            onChange={(event) => setIsConfirmed(event.target.checked)}
          >
            I have reviewed the recipient changes above
          </Checkbox>
          <HStack spacing={1} alignItems='center'>
            <Checkbox
              isChecked={forceApply}
              onChange={(event) => setForceApply(event.target.checked)}
            >
              Force apply recipient changes (override safety checks)
            </Checkbox>
            <InfoHint label='Use only when low/zero eligible counts are expected and intentionally approved. This passes force=true to bypass backend safety conflicts.' />
          </HStack>

          <HStack spacing={3}>
            <Button
              colorScheme='teal'
              variant='solid'
              onClick={onApply}
              isDisabled={!isConfirmed || isApplying || !statementEndDate}
              alignSelf='flex-start'
            >
              Apply Recipient Changes
            </Button>
            <InfoHint label='Writes recipient updates to Postgres by enabling/disabling rentals.is_statements_enabled according to the previewed diff.' />
            {isApplying && <Spinner size='sm' />}
          </HStack>
        </Stack>
      )}
    </Stack>
  )
}

export default ReviewRecipients
